/**
 * Every account in the system, and the two axes an admin can move one along:
 * the global tier (spec §5), and roles granted on a single tournament.
 *
 * The list is Cognito's rather than the profile table's, so an account that
 * signed up and never finished the wizard appears here too — under its email,
 * with no name to show. Those are the accounts this page exists for: until now
 * there was nowhere in the console to see one at all.
 */
import { useRef, useState } from "react";
import type { GlobalRole, TournamentRole } from "@alpha/shared";
import { api, apiErrorMessage } from "../../lib/api";
import {
  useAdminTournaments,
  useAdminUserMutation,
  useAdminUsers,
  useTournamentRoleMutation,
} from "../../lib/adminData";
import { useSession } from "../../lib/me";
import type { AdminUserRow, AdminUserTournamentRole, Tournament } from "../../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  SearchIcon,
  SectionCard,
  Select,
  Skeleton,
  stagger,
  Workbench,
  XIcon,
} from "../../components/ui";

/**
 * Tournament grants in the order the picker offers them, and their labels as a
 * Record so a third role added to the enum breaks the build here rather than
 * showing a director its raw spelling.
 *
 * "Scorer" rather than "Tournament scorer": the chip already names the
 * tournament it sits on, and the pair has to read as one phrase.
 */
const TOURNAMENT_ROLES = ["tournamentAdmin", "tournamentScorer"] as const;
const TOURNAMENT_ROLE_LABEL: Record<TournamentRole, string> = {
  tournamentAdmin: "Tournament admin",
  tournamentScorer: "Scorer",
};

/** The global tiers, coach first — it is the one every account starts in. */
const GLOBAL_ROLES = ["coach", "admin"] as const;
const GLOBAL_ROLE_LABEL: Record<GlobalRole, string> = {
  coach: "Coach",
  admin: "Admin",
};

/*
  A <select> hands back a plain string. These narrowings are not the guard —
  the API revalidates every body against the same enums — they just keep the
  union honest without a cast, and fall back to the value the picker opened on.
*/
const asGlobalRole = (value: string): GlobalRole => (value === "admin" ? "admin" : "coach");
const asTournamentRole = (value: string): TournamentRole =>
  TOURNAMENT_ROLES.find((r) => r === value) ?? "tournamentAdmin";

/** "Jul 26, 2026" from an ISO timestamp; an em dash when Cognito gave none. */
function joinedOn(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * How a row names its account, everywhere it has to: the line on the card, the
 * accessible name of every control on it, and the confirm() before a revoke.
 * Those have to agree on whose access is changing, so they share one
 * derivation.
 *
 * The name leads, unlike the allowlist page, where admin is granted by email
 * and the email is therefore the handle. This list is every account, and a
 * director looking someone up knows them by name. It falls back the same way,
 * ending at the uid an account always has.
 */
const userLabel = (user: AdminUserRow): string => user.name ?? user.email ?? user.uid;

/** Cognito's SHOUTY_SNAKE status as words. The badge re-capitalizes it in CSS. */
const statusLabel = (status: string): string => status.toLowerCase().replace(/_/g, " ");

/**
 * The list in the order it is read, which is by the label each card prints.
 *
 * Sorted here rather than trusted from the server: `GET /admin/users` orders by
 * email first, for parity with the allowlist, where email genuinely is the
 * handle. This page leads with the name (see `userLabel`), and a list ordered
 * on a key it does not show reads as no order at all — Bob Jones above Alice
 * Smith, because their addresses happen to fall that way. Copied before
 * sorting: the array belongs to the query cache.
 */
export function sortUsers(users: AdminUserRow[]): AdminUserRow[] {
  return [...users].sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
}

/**
 * The accounts a search box holding `query` leaves on screen. Name and email
 * only — nobody types a uid into a search box — and split on whitespace, the
 * way a director types one: "zoe example.com" has to narrow, so every term
 * must land somewhere in the pair.
 *
 * Exported because narrowing is the one thing this page does that a static
 * render cannot reach: `query` is state with no way into it from outside.
 */
export function searchUsers(users: AdminUserRow[], query: string): AdminUserRow[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return users;
  return users.filter((user) => {
    const hay = `${user.name ?? ""} ${user.email ?? ""}`.toLowerCase();
    return terms.every((term) => hay.includes(term));
  });
}

/**
 * One grant the account holds, and the way to take it back. Its own component
 * because the revoke mutation is keyed by the tournament the chip names, and a
 * row holding three of them needs three — hooks cannot be called from a loop.
 */
function RoleChip({ uid, who, role }: { uid: string; who: string; role: AdminUserTournamentRole }) {
  const [error, setError] = useState<string>();
  const revoke = useTournamentRoleMutation(role.tournamentId, () =>
    api.del(`/admin/tournaments/${role.tournamentId}/roles/${uid}`),
  );

  // Deleting a tournament clears its grants on both sides, so a nameless chip
  // means the record went missing between those two reads — not that a grant
  // was left behind. Naming it beats rendering "undefined" either way.
  const where = role.tournamentName ?? "Tournament unavailable";
  const what = TOURNAMENT_ROLE_LABEL[role.role];

  return (
    <>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-2.5 pr-1 text-xs">
        <span className="max-w-48 truncate font-medium text-navy-950">{where}</span>
        <span className="text-slate-500">{what}</span>
        <button
          type="button"
          disabled={revoke.isPending}
          aria-label={`Remove ${who} from ${where}`}
          title="Remove this role"
          onClick={() => {
            if (!confirm(`Remove ${what.toLowerCase()} on ${where} for ${who}?`)) return;
            revoke.mutate(undefined, {
              onError: (e) => setError(apiErrorMessage(e, "Could not remove this role.")),
            });
          }}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <XIcon className="h-3 w-3" />
        </button>
      </span>
      {error ? <span className="basis-full text-xs text-red-600">{error}</span> : null}
    </>
  );
}

/**
 * Granting one role on one tournament. Its own component because the mutation
 * is keyed by the tournament picked here, which is row state.
 *
 * The API overwrites a grant rather than refusing a second one, so picking a
 * tournament the account already holds changes the role instead of adding to
 * it — and the button says which of the two is about to happen.
 */
function GrantControl({
  uid,
  who,
  held,
  tournaments,
}: {
  uid: string;
  who: string;
  held: AdminUserTournamentRole[];
  tournaments: Tournament[];
}) {
  const [tid, setTid] = useState("");
  const [role, setRole] = useState<TournamentRole>("tournamentAdmin");
  const [error, setError] = useState<string>();

  const grant = useTournamentRoleMutation(tid, () =>
    api.put(`/admin/tournaments/${tid}/roles/${uid}`, { role }),
  );

  const existing = held.find((r) => r.tournamentId === tid);

  return (
    <form
      className="mt-3 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!tid) return;
        grant.mutate(undefined, {
          onSuccess: () => {
            setTid("");
            setError(undefined);
          },
          onError: (err) => setError(apiErrorMessage(err, "Could not grant this role.")),
        });
      }}
    >
      <Select
        uiSize="sm"
        className="min-w-0 flex-1 basis-48"
        aria-label={`Tournament for ${who}`}
        value={tid}
        onChange={(e) => setTid(e.target.value)}
      >
        <option value="">Tournament…</option>
        {tournaments.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>
      <Select
        uiSize="sm"
        className="w-44 shrink-0"
        aria-label={`Role for ${who}`}
        value={role}
        onChange={(e) => setRole(asTournamentRole(e.target.value))}
      >
        {TOURNAMENT_ROLES.map((r) => (
          <option key={r} value={r}>
            {TOURNAMENT_ROLE_LABEL[r]}
          </option>
        ))}
      </Select>
      <Button type="submit" variant="secondary" size="sm" disabled={!tid || grant.isPending}>
        {grant.isPending ? "Saving…" : existing ? "Change role" : "Grant"}
      </Button>
      {error ? <p className="basis-full text-sm text-red-600">{error}</p> : null}
    </form>
  );
}

function UserRow({
  user,
  isSelf,
  tournaments,
}: {
  user: AdminUserRow;
  isSelf: boolean;
  tournaments: Tournament[];
}) {
  const [error, setError] = useState<string>();

  // A ref, NOT state: the dropdown's onChange fires the mutation in the same
  // handler that records the choice, and a state setter has not applied by
  // then — the mutationFn closure would still PUT the tier the row started on.
  // The status buttons on a tournament carry a ref for the same reason.
  const nextRole = useRef<GlobalRole>("coach");
  const setRole = useAdminUserMutation(() =>
    api.put(`/admin/users/${user.uid}/role`, { role: nextRole.current }),
  );

  const label = userLabel(user);
  const joined = `Joined ${joinedOn(user.createdAt)}`;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-navy-950">
            {label}
            {isSelf ? <span className="ml-2 text-xs font-normal text-slate-400">you</span> : null}
          </p>
          {/* The email goes underneath, unless it is already the line above. */}
          <p className="truncate text-sm text-slate-500">
            {user.email && user.email !== label ? `${user.email} · ${joined}` : joined}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Why a sign-in fails is not something an admin can find anywhere
              else, and it is the first question about an account that cannot
              get in. A confirmed, enabled account says nothing — that is the
              normal case and a badge on every row would drown these two. */}
          {user.enabled ? null : <Badge tone="red">Disabled</Badge>}
          {user.status && user.status !== "CONFIRMED" ? (
            <Badge tone="amber">{statusLabel(user.status)}</Badge>
          ) : null}
          <Select
            uiSize="sm"
            className="w-28"
            aria-label={`Global role for ${label}`}
            title={isSelf ? "You cannot remove your own admin access" : undefined}
            disabled={isSelf || setRole.isPending}
            value={user.isAdmin ? "admin" : "coach"}
            onChange={(e) => {
              nextRole.current = asGlobalRole(e.target.value);
              setRole.mutate(undefined, {
                onError: (err) =>
                  setError(apiErrorMessage(err, "Could not change this account's role.")),
              });
            }}
          >
            {GLOBAL_ROLES.map((r) => (
              <option key={r} value={r}>
                {GLOBAL_ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <span className="text-xs font-medium text-slate-500">Tournament roles</span>
        {user.tournamentRoles.length === 0 ? (
          <span className="text-xs text-slate-400">None</span>
        ) : (
          user.tournamentRoles.map((r) => (
            <RoleChip key={r.tournamentId} uid={user.uid} who={label} role={r} />
          ))
        )}
      </div>

      <GrantControl
        uid={user.uid}
        who={label}
        held={user.tournamentRoles}
        tournaments={tournaments}
      />

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </Card>
  );
}

/**
 * What each role actually reaches. Granting one is a decision about someone
 * else's access, and the four tiers are not guessable from their names — a
 * "tournament admin" sounding like it runs the whole event is exactly the
 * mistake this rail is here to stop.
 */
function RoleLegend() {
  return (
    <SectionCard title="What the roles mean" className="animate-rise" style={stagger(2)}>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="font-medium text-navy-950">Admin</dt>
          <dd className="text-slate-500">
            Everything: every tournament, the venue library, branding, and this page.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-navy-950">Coach</dt>
          <dd className="text-slate-500">
            The default for a new account — their own teams and registrations, and nothing in the
            console.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-navy-950">Tournament admin</dt>
          <dd className="text-slate-500">
            One tournament&rsquo;s details, banner, status, and registration window. Not its
            divisions, entries, schedule, or scores — and it cannot be deleted.
          </dd>
        </div>
        <div>
          <dt className="font-medium text-navy-950">Scorer</dt>
          <dd className="text-slate-500">
            Records results for one tournament&rsquo;s games. Nothing else about the event changes.
          </dd>
        </div>
      </dl>
    </SectionCard>
  );
}

export function AdminUsers() {
  const usersQ = useAdminUsers();
  const tournamentsQ = useAdminTournaments();
  const session = useSession();
  const [query, setQuery] = useState("");

  const users = usersQ.data?.users ?? [];
  const truncated = usersQ.data?.truncated ?? false;
  const tournaments = tournamentsQ.data?.tournaments ?? [];

  const shown = searchUsers(sortUsers(users), query);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        subtitle="Every account that has signed up, and what each one can reach."
      />

      <Workbench aside={<RoleLegend />}>
        <div className="animate-rise space-y-3" style={stagger(1)}>
          {/* The list is every account, not a handful, so the search box is the
              way in rather than an extra. It stays above the states below: a
              slow first load should not move it once the rows arrive. */}
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search accounts"
              placeholder="Search by name or email"
              className="pl-9 [&::-webkit-search-cancel-button]:hidden"
            />
          </div>

          {/* Above the rows, not below them: someone who cannot find an account
              stops looking at the top of the list, and by the time they have
              scrolled past every row they have already concluded it is absent. */}
          {truncated ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This deployment has more accounts than one request can carry, so the list below
              stops partway. An account missing from it may still exist.
            </p>
          ) : null}

          {usersQ.isLoading ? (
            <>
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
            </>
          ) : usersQ.isError ? (
            // Before the empty case, and in red rather than the dashed
            // placeholder that case uses: a failed request has no accounts
            // either, and falling through would say nobody has signed up on the
            // one page whose subject is who has.
            <p className="text-sm text-red-600">
              Couldn&rsquo;t load the account list. Check your connection and try again.
            </p>
          ) : users.length === 0 ? (
            <EmptyState
              title="No accounts yet"
              hint="Everyone who signs up shows here, whether or not they finished setting up."
            />
          ) : shown.length === 0 ? (
            // Distinct from "no accounts yet": the difference between an empty
            // system and a search that missed is the whole reason to say which.
            <EmptyState
              title="No accounts match that search"
              hint={
                truncated
                  ? "Only names and email addresses are searched — and the list is incomplete, so the account may exist beyond it."
                  : "Only names and email addresses are searched."
              }
            />
          ) : (
            shown.map((u) => (
              <UserRow
                key={u.uid}
                user={u}
                isSelf={u.uid === session.data?.uid}
                tournaments={tournaments}
              />
            ))
          )}
        </div>
      </Workbench>
    </div>
  );
}
